import React, { useEffect, useState } from 'react';
import { ActionMode } from '../../../../common/enums/common.enum';
import { Box, Breadcrumbs, Checkbox, CircularProgress, FormControlLabel, IconButton, Link, Typography } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import GenericButton, { GenericButtonTypeEnum } from '../../../../components/GenericButton';
import { ReactComponent as DeleteIcon } from '../../../../assets/delete-icon.svg';
import GenericTextField from '../../../../components/GenericTextField/GenericTextField';
import { KindergartenDto } from '../../../../common/interfaces/kindergarten.interface';
import { getKindergartenLocationsAndGroups } from '../../../../api/kindergarten.api';
import { createNotification } from '../../../../utilities/helper/notification.helper';
import { MESSAGE_TYPE } from '../../../../common/constants/common.constants';
import GenericLocationGroupSelector from '../../../../components/GenericLocationGroupSelector/GenericLocationGroupSelector';
import { CreatePostDto } from '../../../../common/interfaces/infoboard.interface';
import { createPost, deletePost, fetchPostDetails, updatePost } from '../../../../api/infoboard.api';
import GenericInfoModal from '../../../../components/GenericInfoModal';
import { INFOBOARD } from '../../../../common/constants/routes.constants';
import { useTranslation } from 'react-i18next';
// loaders read the i18n instance, not the hook's t: capturing t would make them
// effect dependencies, and re-running a fetch on a language switch buys nothing
import i18n from '../../../../i18n';

interface PostPageProps {
  mode: ActionMode;
}

const PostPage: React.FC<PostPageProps> = ({ mode }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { postId } = useParams<{ postId: string }>();

  const [description, setDescription] = useState('');
  const [important, setImportant] = useState(false);
  const [kindergartensWithLocationsAndGroups, setKindergartensWithLocationsAndGroups] = useState<KindergartenDto[]>([]);
  const [postGroups, setPostGroups] = useState<string[]>([]);

  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchKindergartenWithLocationsAndGroups();
  }, []);

  useEffect(() => {
    if (mode === ActionMode.EDIT && postId) {
      getPostDetails(postId);
    }
  }, [mode, postId]);

  const getPageTitle = () => {
    switch (mode) {
      case ActionMode.CREATE:
        return t('infoboard.createPost');
      case ActionMode.EDIT:
        return t('infoboard.editPost');
    }
  };
  const fetchKindergartenWithLocationsAndGroups = async () => {
    try {
      const response = await getKindergartenLocationsAndGroups();

      const kindergartens = (response.results ?? []).map(k => ({
        ...k,
        locations: (k.locations ?? []).filter(loc => loc.groups && loc.groups.length > 0)
      }));
      const filteredKindergartens = kindergartens.filter(k => k.locations.length > 0);
      setKindergartensWithLocationsAndGroups(filteredKindergartens ?? []);
    } catch (error) {
      createNotification(MESSAGE_TYPE.ERROR, i18n.t('groups.loadFailed'));
    }
  };

  const getPostDetails = async (postId: string) => {
    try {
      setLoading(true);
      const response = await fetchPostDetails(postId);
      const post = response.results;
      if (!post) throw new Error(i18n.t('infoboard.postLoadFailed'));
      setDescription(post.description);
      setImportant(post.important);
      setPostGroups(post.groupIds || []);
    } catch (error) {
      createNotification(MESSAGE_TYPE.ERROR, i18n.t('infoboard.postLoadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleSavePost = async () => {
    try {
      const createPostDto: CreatePostDto = {
        description: description,
        important: important,
        groupIds: postGroups
      };
      await createPost(createPostDto);

      createNotification(MESSAGE_TYPE.SUCCESS, t('infoboard.postCreated'));
      setDescription('');
      setImportant(false);
      setPostGroups([]);
      navigate(INFOBOARD);
    } catch (error) {
      createNotification(MESSAGE_TYPE.ERROR, t('infoboard.postCreateFailed'));
    }
  };

  const handleUpdatePost = async (postId: string) => {
    try {
      const updatePostDto: CreatePostDto = {
        description: description,
        important: important,
        groupIds: postGroups
      };
      await updatePost(postId, updatePostDto);
      createNotification(MESSAGE_TYPE.SUCCESS, t('infoboard.postUpdated'));
      setDescription('');
      setImportant(false);
      setPostGroups([]);
      navigate(INFOBOARD);
    } catch (error) {
      createNotification(MESSAGE_TYPE.ERROR, t('infoboard.postUpdateFailed'));
    }
  };

  const removePost = async (postId: string) => {
    try {
      await deletePost(postId);
      navigate(INFOBOARD);
      createNotification(MESSAGE_TYPE.SUCCESS, t('infoboard.postDeleted'));
    } catch (error) {
      createNotification(MESSAGE_TYPE.ERROR, t('infoboard.postDeleteFailed'));
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%'
        }}
      >
        <CircularProgress />
      </Box>
    );
  }
  return (
    <Box p={7}>
      <Breadcrumbs aria-label={t('common.breadcrumb')} sx={{ mb: 3 }}>
        <Link color="text.secondary" underline="hover" onClick={() => navigate('/infoboard')} sx={{ cursor: 'pointer' }}>
          {t('nav.infoboard')}
        </Link>
        <Typography color="text.primary">{getPageTitle()}</Typography>
      </Breadcrumbs>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb="44px">
        <Typography variant="h1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {mode === ActionMode.CREATE && t('infoboard.createPost')}
          {mode === ActionMode.EDIT && t('infoboard.editPost')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          {mode === ActionMode.EDIT && (
            <IconButton onClick={() => setOpenConfirmModal(true)}>
              <DeleteIcon width="2<BasicTable8px" height="28px" />
            </IconButton>
          )}
          <GenericButton
            buttonType={GenericButtonTypeEnum.Primary}
            variant="contained"
            onAsyncAction={async () => {
              if (mode === ActionMode.CREATE) {
                await handleSavePost();
              } else if (mode === ActionMode.EDIT && postId) {
                await handleUpdatePost(postId);
              }
            }}
            label={t('common.save')}
            disabled={!description.trim() || postGroups.length === 0}
          />
        </Box>
      </Box>

      <Box display="flex" gap={6} alignItems="flex-start">
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            gap: 3
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              flex: 1,
              gap: 1.5
            }}
          >
            <Typography variant="caption">{t('infoboard.postContent')}</Typography>
            <Box sx={{ width: '100%' }}>
              <GenericTextField
                label={t('common.description')}
                multiline
                minRows={4}
                maxRows={10}
                value={description}
                onChange={e => setDescription(e.target.value)}
              />
            </Box>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              flex: 1
            }}
          >
            <Typography variant="caption">{t('infoboard.postImportance')}</Typography>
            <FormControlLabel
              control={<Checkbox checked={important} onChange={e => setImportant(e.target.checked)} />}
              label={t('infoboard.markAsImportant')}
            />
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            gap: 1.5
          }}
        >
          <Typography variant="caption">{t('infoboard.postVisibility')}</Typography>
          <GenericLocationGroupSelector
            kindergartens={kindergartensWithLocationsAndGroups}
            withKindergarten={true}
            selectedGroups={postGroups}
            onChange={group => setPostGroups(group)}
            isPost
          />
        </Box>
      </Box>
      <GenericInfoModal
        open={openConfirmModal}
        onClose={() => setOpenConfirmModal(false)}
        onConfirm={() => removePost(postId!)}
        label={t('infoboard.deletePost')}
        message={t('infoboard.deletePostConfirm')}
        confirmLabel={t('common.delete')}
        entityName={''}
      />
    </Box>
  );
};
export default PostPage;
